import { TableRow, TableCell, Box } from '@mui/material';
import type { HeaderGroup } from '@tanstack/react-table';
import type { ColumnMetadata, LookupDataItem } from '../../../types';
import type { FilterOperators, FilterMenuAnchor } from '../types';
import { FilterCell } from '../filter-inputs';
import { isUtilityGridColumn } from '../utils';
import type React from 'react';

type DataGridFilterRowProps<T> = {
  headerGroup: HeaderGroup<T>;
  columnMetadataMap: Map<string, ColumnMetadata>;
  filterOperators: FilterOperators;
  setFilterMenuAnchor: React.Dispatch<React.SetStateAction<FilterMenuAnchor>>;
  lookupMaps?: Map<string, Record<string, string>>;
  rawLookupItems?: Map<string, LookupDataItem[]>;
  manualFiltering?: boolean;
  headerHeight: number;
  hiddenColumnIds?: Set<string>;
};

/** Height of the filter row in px — must be explicit so that
 *  absolutely-positioned filter inputs have a non-zero containing block. */
const FILTER_ROW_HEIGHT = 36;

/**
 * DataGrid filter row component
 * Renders filter inputs for each column below the header
 */
export const DataGridFilterRow = <T,>({
  headerGroup,
  columnMetadataMap,
  filterOperators,
  setFilterMenuAnchor,
  lookupMaps,
  rawLookupItems,
  manualFiltering,
  headerHeight,
  hiddenColumnIds,
}: DataGridFilterRowProps<T>) => {
  return (
    <TableRow key={`${headerGroup.id}-filter`} sx={{ height: `${FILTER_ROW_HEIGHT}px` }}>
      {headerGroup.headers
        .filter((header) => !hiddenColumnIds?.has(header.column.id))
        .map((header) => {
          const columnId = header.column.id;
          const columnMeta = columnMetadataMap.get(columnId);
          const rawFilterValue = header.column.getFilterValue();
          const filterValue =
            rawFilterValue != null && typeof rawFilterValue === 'object'
              ? ((rawFilterValue as { val?: string }).val ?? '')
              : ((rawFilterValue as string) ?? '');

          if (isUtilityGridColumn(columnId)) {
            return (
              <TableCell
                key={`${header.id}-filter`}
                sx={{
                  backgroundColor: 'background.paper',
                  padding: '0 4px',
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.text.secondary
                      : theme.palette.text.primary,
                  position: 'sticky',
                  top: `${headerHeight}px`,
                  zIndex: 11,
                  height: `${FILTER_ROW_HEIGHT}px`,
                }}
              />
            );
          }

          return (
            <TableCell
              key={`${header.id}-filter`}
              sx={{
                backgroundColor: 'background.paper',
                padding: 0,
                color: (theme) =>
                  theme.palette.mode === 'dark'
                    ? theme.palette.text.secondary
                    : theme.palette.text.primary,
                position: 'sticky',
                top: `${headerHeight}px`,
                zIndex: 11,
                height: `${FILTER_ROW_HEIGHT}px`,
                overflow: 'visible',
              }}
            >
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                <FilterCell
                  header={header}
                  columnMetadata={columnMeta}
                  filterValue={filterValue}
                  filterOperators={filterOperators}
                  setFilterMenuAnchor={setFilterMenuAnchor}
                  lookupMaps={lookupMaps}
                  rawLookupItems={rawLookupItems}
                  manualFiltering={manualFiltering}
                />
              </Box>
            </TableCell>
          );
        })}
    </TableRow>
  );
};
